/** WCAG 2.1 relative-luminance contrast, for the theme settings color picker's warning.
    No dependency: the math is short and the app doesn't need it anywhere else. */

const SRGB_CHANNEL = 255;
const GAMMA_THRESHOLD = 0.03928;
const GAMMA_DIVISOR = 12.92;
const GAMMA_OFFSET = 0.055;
const GAMMA_SCALE = 1.055;
const GAMMA_EXPONENT = 2.4;
export const AA_TEXT_CONTRAST = 4.5;

function channel(value: number): number {
  const c = value / SRGB_CHANNEL;
  return c <= GAMMA_THRESHOLD
    ? c / GAMMA_DIVISOR
    : ((c + GAMMA_OFFSET) / GAMMA_SCALE) ** GAMMA_EXPONENT;
}

/** `#RRGGBB` -> relative luminance in [0, 1]; throws on anything else (the API already
    rejects a malformed hex before this ever runs on a saved value). */
export function relativeLuminance(hex: string): number {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  const value = match?.[1];
  if (!value) throw new Error(`not a 6-digit hex color: ${hex}`);
  const r = channel(parseInt(value.slice(0, 2), 16));
  const g = channel(parseInt(value.slice(2, 4), 16));
  const b = channel(parseInt(value.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The WCAG contrast ratio between two colors, always >= 1 regardless of argument order. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** `id` keys into `themeSettings.contrastLabels.*` in the i18n catalogs; callers translate
    it for display rather than showing this English id directly. */
export type ContrastCheckId = "headingOnBg" | "bodyOnBg" | "headingOnSurface" | "accentOnBg";
export type ContrastCheck = { id: ContrastCheckId; ratio: number; passes: boolean };

/** The pairs that matter for a readable console: body text and headings against the two
    surfaces they sit on, plus the accent color against the page background. */
export function checkThemeContrast(colors: {
  bg: string;
  surface: string;
  textHi: string;
  textMid: string;
  accent: string;
}): ContrastCheck[] {
  const pairs: [ContrastCheckId, string, string][] = [
    ["headingOnBg", colors.textHi, colors.bg],
    ["bodyOnBg", colors.textMid, colors.bg],
    ["headingOnSurface", colors.textHi, colors.surface],
    ["accentOnBg", colors.accent, colors.bg],
  ];
  return pairs.map(([id, fg, bg]) => {
    const ratio = contrastRatio(fg, bg);
    return { id, ratio, passes: ratio >= AA_TEXT_CONTRAST };
  });
}
